import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertCircle,
  Bell,
  BellRing,
  ExternalLink,
  LogOut,
  Settings,
  User,
} from "lucide-react";
import React from "react";
import { useAuth } from "react-oidc-context";
import { NavLink } from "react-router-dom";
import { usePushNotifications } from "../hooks/use-push-notifications";
import LoadingOverlay from "./LoadingOverlay";
import Typography from "./Typography";
import Divider from "./Divider";
const pages: {
  name: string;
  url: string;
  icon: React.ReactNode;
}[] = [];

export function TopMenu() {
  const auth = useAuth();

  const {
    isSupported,
    permissionStatus,
    subscriptionMutation,
    subscriptionStatus,
    unsubscriptionMutation,
  } = usePushNotifications();

  const isMutatingNotifications =
    subscriptionMutation.isPending || unsubscriptionMutation.isPending;
  const isSubscribed = subscriptionMutation.isPending
    ? true
    : unsubscriptionMutation.isPending
      ? false
      : !!subscriptionStatus.data;

  return (
    <TooltipProvider>
      <nav className="TopMenu">
        <div className="Container TopMenu__container">
          <div className="TopMenu__left-section">
            <NavLink to="/" className="TopMenu__logo">
              <img src={import.meta.env.VITE_LOGO_IMAGE_NAME} alt={import.meta.env.VITE_APP_NAME ?? "Logo"} />
            </NavLink>
            <div className="TopMenu__links">
              {pages.map((page) => (
                <NavLink
                  key={page.name}
                  className={({ isActive, isPending }) =>
                    "TopMenu__links__item" +
                    (isPending ? " pending" : isActive ? " active" : "")
                  }
                  to={page.url}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost">
                        {page.icon}
                        <Typography variant="body1">{page.name}</Typography>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{page.name}</TooltipContent>
                  </Tooltip>
                </NavLink>
              ))}
            </div>
          </div>
          <div className="TopMenu__right-section">
            {!isSupported && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <AlertCircle className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Notifications unavailable</TooltipContent>
              </Tooltip>
            )}
            {isSupported && permissionStatus === "denied" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => subscriptionMutation.mutate()}
                  >
                    <AlertCircle className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Notifications forbidden</TooltipContent>
              </Tooltip>
            )}
            {isSupported && isMutatingNotifications && (
              <Button variant="ghost" size="icon" disabled>
                <LoadingOverlay />
              </Button>
            )}
            {isSupported && !isMutatingNotifications && isSubscribed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => unsubscriptionMutation.mutate()}
                  >
                    <BellRing className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Disattiva notifiche</TooltipContent>
              </Tooltip>
            )}
            {isSupported && !isMutatingNotifications && !isSubscribed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => subscriptionMutation.mutate()}
                  >
                    <Bell className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Attiva notifiche</TooltipContent>
              </Tooltip>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <a href={`https://home.${__CLIENT__}.it`}>
                    <ExternalLink className="h-4 w-4" />
                    Profilo
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NavLink to="/settings">
                    <Settings className="h-4 w-4" />
                    Impostazioni
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    auth.signoutRedirect({
                      extraQueryParams: {
                        client_id: auth.settings.client_id,
                        logout_uri: auth.settings.post_logout_redirect_uri!,
                      },
                      id_token_hint: auth.user?.id_token,
                    })
                  }
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </DropdownMenuItem>
                {/* Divider con numero di versione dell'applicazione */}
                <Divider />
                <DropdownMenuItem disabled style={{
                  justifyContent: "center"
                }}>
                  <Typography variant="body2" className="TopMenu__version" style={{textAlign: "center"}}>
                    {window.location.hostname.includes("localhost")
                      ? "v.local"
                      : __APP_VERSION__}
                  </Typography>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>
    </TooltipProvider>
  );
}
